// Shared by the public status page and the admin incidents page: level/impact metadata and the update timeline.
import type { Incident, IncidentImpact, IncidentStatus, IncidentUpdate, StatusLevel } from "../../../shared/types.ts";
import { fmtDateTime } from "../../format.ts";
import { cn } from "../../ui/index.tsx";

// Class names are written out in full so Tailwind can see them.
export interface LevelMeta {
  /** Banner headline for the overall state. */
  banner: string;
  /** Per-component label. */
  label: string;
  text: string;
  bg: string;
  panel: string;
}

export const LEVELS: Record<StatusLevel, LevelMeta> = {
  operational: { banner: "All systems operational", label: "Operational", text: "text-ok", bg: "bg-ok", panel: "border-ok/30 bg-ok/5" },
  maintenance: { banner: "Under maintenance", label: "Under maintenance", text: "text-info", bg: "bg-info", panel: "border-info/30 bg-info/5" },
  degraded: { banner: "Degraded performance", label: "Degraded performance", text: "text-warn", bg: "bg-warn", panel: "border-warn/30 bg-warn/5" },
  partial: { banner: "Partial outage", label: "Partial outage", text: "text-ember", bg: "bg-ember", panel: "border-ember/30 bg-ember/5" },
  major: { banner: "Major outage", label: "Major outage", text: "text-bad", bg: "bg-bad", panel: "border-bad/30 bg-bad/5" },
};

export const LEVEL_ORDER: StatusLevel[] = ["operational", "maintenance", "degraded", "partial", "major"];

interface ImpactMeta { label: string; text: string; badge: string; border: string }
export const IMPACTS: Record<IncidentImpact, ImpactMeta> = {
  maintenance: { label: "Maintenance", text: "text-info", badge: "bg-info/10 text-info border-info/25", border: "border-info/40" },
  minor: { label: "Minor", text: "text-warn", badge: "bg-warn/10 text-warn border-warn/25", border: "border-warn/40" },
  major: { label: "Major", text: "text-ember", badge: "bg-ember/10 text-ember border-ember/25", border: "border-ember/40" },
  critical: { label: "Critical", text: "text-bad", badge: "bg-bad/10 text-bad border-bad/25", border: "border-bad/40" },
};

export const INCIDENT_STATUSES: IncidentStatus[] = ["investigating", "identified", "monitoring", "scheduled", "resolved"];

export const STATUS_LABEL: Record<IncidentStatus, string> = {
  investigating: "Investigating",
  identified: "Identified",
  monitoring: "Monitoring",
  resolved: "Resolved",
  scheduled: "Scheduled",
};

const STATUS_DOT: Record<IncidentStatus, string> = {
  investigating: "bg-bad",
  identified: "bg-warn",
  monitoring: "bg-info",
  resolved: "bg-ok",
  scheduled: "bg-info",
};

const badgeBase = "inline-flex items-center gap-1.5 rounded-md border px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase leading-4 tracking-wider whitespace-nowrap";

export function ImpactBadge({ impact }: { impact: IncidentImpact }) {
  return <span className={cn(badgeBase, IMPACTS[impact].badge)}>{IMPACTS[impact].label}</span>;
}

export function IncidentStatusBadge({ status }: { status: IncidentStatus }) {
  const cls = status === "resolved" ? "bg-ok/10 text-ok border-ok/25" : status === "monitoring" || status === "scheduled" ? "bg-info/10 text-info border-info/25" : status === "identified" ? "bg-warn/10 text-warn border-warn/25" : "bg-bad/10 text-bad border-bad/25";
  return <span className={cn(badgeBase, cls)}>{STATUS_LABEL[status]}</span>;
}

/** Newest-first list of updates, drawn as a vertical timeline. */
export function UpdateTimeline({ updates }: { updates: IncidentUpdate[] }) {
  if (updates.length === 0) return null;
  return (
    <ol className="space-y-4 border-l border-line pl-5">
      {updates.map((u) => (
        <li key={u.id} className="relative">
          <span className={cn("absolute -left-[25px] top-1.5 size-2 rounded-full ring-4 ring-surface", STATUS_DOT[u.status])} />
          <p className="text-sm leading-relaxed text-muted">
            <strong className="font-semibold text-fg">{STATUS_LABEL[u.status]}</strong>
            <span className="mx-1.5 text-faint">-</span>
            <span className="whitespace-pre-line">{u.message}</span>
          </p>
          <time className="mt-1 block font-mono text-[11px] text-faint">{fmtDateTime(u.created_at)}</time>
        </li>
      ))}
    </ol>
  );
}

export const incidentScope = (i: Pick<Incident, "game_id" | "game_name">) => (i.game_id ? i.game_name ?? i.game_id : "All games");
